import { defineMessages, enPlural, ruPlural } from '@/i18n'

/** Тексты AI-панели редактора: композер, область правки, итог прогона. */
export const messages = defineMessages({
  ru: {
    countedGeneration: (c: string) => `Сделано ${c}`,
    countedImages: (c: string) => `Нарисовано ${c}`,
    countedBlockEdit: (c: string) => `Переписано ${c}`,
    countedTranslation: (c: string) => `Переведено ${c}`,
    doneOfTotal: (done: number, total: number | string) => `${done} из ${total}`,
    vanished: 'место исчезло',
    outcomeNotStarted: 'Запуск не начался, кредиты не списаны',
    outcomeInterrupted: (phrase: string) => `Запуск прерван, ${phrase}`,
    outcomeCancelled: (phrase: string) => `Запуск отменён, ${phrase}`,
    emptyRunReason:
      'Ни одна картинка не нарисовалась. Так бывает, когда модель отказалась рисовать сюжет, когда промпт у места исчез до старта запуска или когда не открылась исходная картинка. Переформулируйте промпт и запустите отрисовку ещё раз — кредиты за ненарисованное не списаны.',
    phProcessing: 'Обработка...',
    phGenerating: 'Генерация...',
    phBusy: 'Курс занят другим запуском',
    underReview: 'Курс на проверке',
    phGenerate: 'Опишите тему курса...',
    phEdit: 'Опишите что изменить...',
    phLoadingBlocks: 'Загружаю блоки урока...',
    phSelectBlock: 'Сначала выберите блок справа...',
    phNoBlocks: 'В уроке пока нет блоков',
    phIdle: 'Выберите действие внизу',
    modeGenerate: 'Создать курс',
    modeEditBlock: 'Ред. блок',
    send: 'Отправить',
    aiUnavailable: 'AI-ассистент пока недоступен',
    aiUnavailableHint: 'Появится в следующих обновлениях',
    lessonBlockPrefix: (lesson: number, block: number) => `Урок ${lesson} · Блок ${block} · `,
    scopeLesson: (n: number) => `Весь урок · блоков: ${n}`,
    scopeSelection: (n: number) => `Выбрано · блоков: ${n}`,
    busyAwaiting: (jobId: number) => `Запуск #${jobId} ждёт вашего ответа`,
    busyCourse: (jobId: number, jobType: string) => `Курс занят запуском #${jobId} (${jobType})`,
    answerQuestion: 'Ответить на вопрос',
    showRun: 'Показать запуск',
    emptyState: 'Нажмите «Создать курс» внизу — AI напишет уроки за вас',
    jobLostTitle: 'Запуск больше не доступен',
    jobLostText:
      'Сервер не отдаёт этот запуск — его история недоступна. То, что успело записаться, уже в курсе: проверьте таймлайн.',
    gotIt: 'Понятно',
    drawImages: 'Нарисовать картинки',
    lessonCount: 'Кол-во уроков',
    editScope: 'Область правки',
    scopeBlock: 'Этот блок',
    scopeWholeLesson: 'Весь урок',
    scopeSelected: 'Выбранные',
    loadingLessonBlocks: 'Загружаю блоки урока…',
    scopeBlockHint: 'Чтобы править «Этот блок», сначала выделите блок в уроке.',
    scopeLessonHint:
      'В уроке пока нет блоков — «Весь урок» станет доступен, когда появится хотя бы один.',
    scopeSelectionHint:
      'Чтобы править «Выбранные», отметьте блоки в уроке — Ctrl/Cmd + клик по миниатюре.',
    blocksInEdit: (n: number) => `Блоков в правке: ${n}`,
    customBadgeWarning:
      'В области есть бейдж с кастомной иконкой — сервер не гарантирует её сохранность: ссылка уцелеет, только если модель совсем не тронет этот блок.',
    resumedNotice: 'Запуск продолжается: доделываю то, что осталось. Записанное не переделывается.',
    translatedNotice: 'Текст переведён. Дорисовываю картинки второго языка…',
    retryUnits: 'Эти единицы запуск попробует ещё раз:',
    alreadyResumed: 'Этот запуск уже продолжается — переключаю на продолжение.',
    showResumed: 'Показать продолжение',
    resuming: 'Продолжаю…',
    resume: 'Продолжить',
    tryAgain: 'Попробовать снова',
    stepsLeft: (n: number) => `Осталось доделать ${n} ${ruPlural(n, 'шаг', 'шага', 'шагов')}.`,
    close: 'Закрыть',
    imagePromptNote:
      'Если вы просили поменять картинку — обновился только промпт, сама картинка осталась прежней.',
    charged: (credits: string) => `Списано ${credits}`,
    finishedAt: (time: string) => `Завершено ${time}`,
  },
  en: {
    countedGeneration: (c: string) => `${c} done`,
    countedImages: (c: string) => `${c} generated`,
    countedBlockEdit: (c: string) => `${c} rewritten`,
    countedTranslation: (c: string) => `${c} translated`,
    doneOfTotal: (done: number, total: number | string) => `${done} of ${total}`,
    vanished: 'spot no longer exists',
    outcomeNotStarted: 'The run didn’t start — no credits were charged',
    outcomeInterrupted: (phrase: string) => `Run interrupted — ${phrase}`,
    outcomeCancelled: (phrase: string) => `Run canceled — ${phrase}`,
    emptyRunReason:
      'No images were generated. This happens when the model refuses to draw the scene, when a spot’s prompt disappeared before the run started, or when the source image couldn’t be opened. Rephrase the prompt and run image generation again — you weren’t charged for images that weren’t generated.',
    phProcessing: 'Processing...',
    phGenerating: 'Generating...',
    phBusy: 'The course is busy with another run',
    underReview: 'Course under review',
    phGenerate: 'Describe the course topic...',
    phEdit: 'Describe what to change...',
    phLoadingBlocks: 'Loading lesson blocks...',
    phSelectBlock: 'Select a block on the right first...',
    phNoBlocks: 'This lesson has no blocks yet',
    phIdle: 'Choose an action below',
    modeGenerate: 'Create course',
    modeEditBlock: 'Edit block',
    send: 'Send',
    aiUnavailable: 'The AI assistant isn’t available yet',
    aiUnavailableHint: 'Coming in a future update',
    lessonBlockPrefix: (lesson: number, block: number) => `Lesson ${lesson} · Block ${block} · `,
    scopeLesson: (n: number) => `Whole lesson · ${n} ${enPlural(n, 'block', 'blocks')}`,
    scopeSelection: (n: number) => `Selected · ${n} ${enPlural(n, 'block', 'blocks')}`,
    busyAwaiting: (jobId: number) => `Run #${jobId} is waiting for your answer`,
    busyCourse: (jobId: number, jobType: string) =>
      `The course is busy with run #${jobId} (${jobType})`,
    answerQuestion: 'Answer the question',
    showRun: 'Show run',
    emptyState: 'Click “Create course” below — AI will write the lessons for you',
    jobLostTitle: 'The run is no longer available',
    jobLostText:
      'The server no longer returns this run — its history is unavailable. Whatever was saved is already in the course: check the timeline.',
    gotIt: 'Got it',
    drawImages: 'Generate images',
    lessonCount: 'Number of lessons',
    editScope: 'Edit scope',
    scopeBlock: 'This block',
    scopeWholeLesson: 'Whole lesson',
    scopeSelected: 'Selected',
    loadingLessonBlocks: 'Loading lesson blocks…',
    scopeBlockHint: 'To edit “This block”, select a block in the lesson first.',
    scopeLessonHint:
      'This lesson has no blocks yet — “Whole lesson” becomes available once there’s at least one.',
    scopeSelectionHint:
      'To edit “Selected”, mark blocks in the lesson — Ctrl/Cmd + click a thumbnail.',
    blocksInEdit: (n: number) => `Blocks to edit: ${n}`,
    customBadgeWarning:
      'The scope includes a badge with a custom icon — the server can’t guarantee it’s kept: the link survives only if the model doesn’t touch that block at all.',
    resumedNotice:
      'The run is continuing: finishing what’s left. Work already saved won’t be redone.',
    translatedNotice: 'Text translated. Generating images for the second language…',
    retryUnits: 'The run will retry these items:',
    alreadyResumed: 'This run is already being continued — switching to the continuation.',
    showResumed: 'Show continuation',
    resuming: 'Continuing…',
    resume: 'Continue',
    tryAgain: 'Try again',
    stepsLeft: (n: number) => `${n} ${enPlural(n, 'step', 'steps')} left to finish.`,
    close: 'Close',
    imagePromptNote:
      'If you asked to change an image, only the prompt was updated — the image itself stayed the same.',
    charged: (credits: string) => `Charged ${credits}`,
    finishedAt: (time: string) => `Finished ${time}`,
  },
})
